import { useState, type FormEvent } from 'react';

import { categoryRequestSchema, type CategoryRequestResponse } from '@lifetimeline/shared';

import { Button } from '../../components/Button.js';
import { Field, TextareaField } from '../../components/Field.js';
import { Notice } from '../../components/Notice.js';
import { useI18n } from '../../i18n/index.js';
import { api, errorMessageKey } from '../../lib/apiClient.js';

/**
 * 없는 카테고리 요청.
 *
 * 사용자가 카테고리를 직접 만들게 하지 않는 대신 요청을 받는다. Custom을
 * 허용하면 `키보드`/`기계식 키보드`/`Keyboard`가 난립해 "한 카테고리에 하나"
 * 규칙이 무너진다.
 *
 * **비로그인도 보낼 수 있다** — 막히는 건 대개 처음 온 사람이다.
 */
export function CategoryRequestForm({
  initialText,
  onDone,
}: {
  initialText: string;
  onDone: () => void;
}) {
  const { t, language } = useI18n();
  const [text, setText] = useState(initialText);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const parsed = categoryRequestSchema.safeParse({
      text: text.trim(),
      ...(note.trim() === '' ? {} : { note: note.trim() }),
      language,
    });
    if (!parsed.success) {
      setError(t('error.validation'));
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post<CategoryRequestResponse>(
        '/api/category-requests',
        parsed.data,
      );
      setDone(response.count);
    } catch (cause) {
      setError(t(errorMessageKey(cause)));
    } finally {
      setSubmitting(false);
    }
  };

  if (done !== null) {
    return (
      <div>
        <Notice tone="info">
          <strong>{t('categoryRequest.doneTitle')}</strong>
          <br />
          {t('categoryRequest.doneBody')}
          {/* 같은 요청이 여러 번 들어왔다면 알려준다 — 혼자가 아니라는 신호 */}
          {done > 1 ? <> {t('categoryRequest.doneCount', { count: done })}</> : null}
        </Notice>
        <div style={{ marginTop: 'var(--s5)' }}>
          <Button fullWidth onClick={onDone}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)', marginBottom: 'var(--s5)' }}>
        {t('categoryRequest.lead')}
      </p>

      <Field
        label={t('categoryRequest.nameLabel')}
        placeholder={t('categoryRequest.namePlaceholder')}
        value={text}
        maxLength={50}
        autoFocus
        onChange={(event) => setText(event.target.value)}
      />

      <TextareaField
        label={t('categoryRequest.noteLabel')}
        placeholder={t('categoryRequest.notePlaceholder')}
        value={note}
        maxLength={300}
        onChange={(event) => setNote(event.target.value)}
      />

      {error !== null ? <Notice tone="error">{error}</Notice> : null}

      <div style={{ marginTop: 'var(--s5)' }}>
        <Button type="submit" fullWidth loading={submitting} disabled={text.trim() === ''}>
          {t('categoryRequest.submit')}
        </Button>
      </div>
    </form>
  );
}
